// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import Badge from 'components/treactui-template/atoms/Badge';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import cs from 'classnames';
import IconFilter from 'components/treactui-template/icons/IconFilter';
import AnimateHeight from 'react-animate-height';
import { DataFilter } from './filter/filters-defaults';
import IconTiles from 'components/treactui-template/icons/IconTiles';
import DataFilters from './filter/DataFilter';
import IconList from 'components/treactui-template/icons/IconList';
import { Button } from '@tls/treact-ui';

interface Props {
  filter: DataFilter;
  viewType: 'grid' | 'table';
  setFilter: (filter: DataFilter) => void;
  setViewType: (viewType: 'grid' | 'table') => void;
  projectTags: string[];
}

export default function DataHeader({ filter, viewType, setFilter, setViewType, projectTags }: Readonly<Props>) {
  const [showFilter, setShowFilter] = useState<boolean>(false);

  const activeFilterCount = useMemo(() => {
    return [filter.dataset, filter.processTag, filter.label, filter.imageTag].reduce(
      (sum, current) => sum + (current?.length ? 1 : 0),
      0
    );
  }, [filter]);

  return (
    <Root>
      <Actions>
        <Button
          icon={<IconTiles width='1.5rem' key='filter-btn-key' />}
          className={cs({
            selected: viewType === 'grid',
          })}
          variant='secondary'
          onClick={() => setViewType('grid')}
          data-testid='data-view-grid-button'
        />
        <Button
          icon={<IconList width='1.5rem' key='filter-btn-key' />}
          className={cs({
            selected: viewType === 'table',
          })}
          variant='secondary'
          onClick={() => setViewType('table')}
          data-testid='data-view-table-button'
        />
        <div style={{ width: '1rem' }} />
        <Badge value={activeFilterCount} display={activeFilterCount > 0}>
          <Button
            icon={<IconFilter width='1.5rem' key='filter-btn-key' />}
            className={cs({
              selected: showFilter,
            })}
            variant='secondary'
            onClick={() => setShowFilter(!showFilter)}
            data-testid='data-filter-toggle-button'
          />
        </Badge>
      </Actions>
      <AnimateHeight duration={100} height={showFilter ? 'auto' : 0}>
        <Filters>
          <DataFilters filter={filter} setFilter={setFilter} setShowFilter={setShowFilter} projectTags={projectTags} />
        </Filters>
      </AnimateHeight>
    </Root>
  );
}

const Root = styled.div`
  padding: 1rem;
`;
const Actions = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
`;
const Filters = styled.div`
  display: flex;
  margin-top: 1rem;
  justify-content: end;
  align-items: center;
  gap: 8px;
`;
